import { API_BASE_URL } from './client';


// User Management API
export const getUsersPage = async (keycloak, { search = null, first = 0, max = 100 } = {}) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const params = new URLSearchParams({ first: String(first), max: String(max) });
  if (search) params.append('search', search);

  const response = await fetch(`${API_BASE_URL}/users/?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch users');
  }
  const data = await response.json();
  if (Array.isArray(data)) {
    return { items: data, total: data.length, limit: max, offset: first };
  }
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total) || 0,
    limit: Number(data.limit) || max,
    offset: Number(data.offset) || first,
  };
};

/** Returns a plain array for legacy bulk-load call sites. */
export const getUsers = async (keycloak, search = null, first = 0, max = 100) => {
  const { items } = await getUsersPage(keycloak, { search, first, max });
  return items;
};

export const getUser = async (keycloak, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch user');
  }
  return await response.json();
};

export const getUserProfile = async (keycloak, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/profile/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch user profile');
  }
  return await response.json();
};

export const createUser = async (keycloak, userData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create user');
  }
  return await response.json();
};

export const updateUser = async (keycloak, userId, userData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update user');
  }
  return await response.json();
};

export const deleteUser = async (keycloak, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete user');
  }
  return await response.json();
};

export const resetUserPassword = async (keycloak, userId, password, temporary = false) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/reset-password/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password, temporary }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reset password');
  }
  return await response.json();
};

export const toggleUserEnabled = async (keycloak, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/${userId}/toggle-enabled/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to toggle user status');
  }
  return await response.json();
};

export const bulkAddUsersToGroup = async (keycloak, userIds, groupId, role = 'member') => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/bulk/add-to-group/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_ids: userIds, group_id: groupId, role }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add users to group');
  }
  return await response.json();
};

export const getCurrentUserInfo = async (keycloak) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/me/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch current user info');
  }
  return await response.json();
};

export const getCurrentUserRoles = async (keycloak) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/users/me/roles/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch user roles');
  }
  return await response.json();
};
